/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import { Navigate, Outlet, createBrowserRouter, RouterProvider, json } from 'react-router-dom';
import { isAxiosError } from 'axios';
import Sidebar from './components/Sidebar';
import LandingChatPage from './pages/LandingChatPage';
import BrandDetailPage from './pages/BrandDetailPage';
import SegmentsPage from './pages/SegmentsPage';
import PromotionsPage from './pages/PromotionsPageV2';
import InsightsPage from './pages/InsightsPage';
import ErrorPage from './pages/ErrorPage';
import NotFoundPage from './pages/NotFoundPage';
import { ChatProvider } from './contexts/ChatContext';
import { isDemoMode } from './services';
import ExecutiveDashboard from './pages/dashboard/ExecutiveDashboard';
import DeepDiveDashboard from './pages/dashboard/DeepDiveDashboard';
import DigitalMetricsDashboard from './pages/dashboard/DigitalMetricsDashboard';
import BrandComparison from './pages/dashboard/BrandComparison';
import PostsDashboard from './pages/dashboard/PostsDashboard';
import DashboardLayout from './pages/dashboard/DashboardLayout';
import { Auth0ProviderWithNavigate } from './auth';
import { getMeApiV3MeGet } from './config/api';
import { getAccessTokenForLoader, redirectToLogin } from './auth/auth0-client';
import { setTokenGetter } from './config/axios.config';
import { auth0Client } from './auth/auth0-client';
import { queryClient } from '.';
import { getMeApiV3MeGetOptions } from './config/api/@tanstack/react-query.gen';

/**
 * Layout wrapper with sidebar
 * Uses theme-aware background colors via CSS variables
 */
const AppLayout: React.FC = () => {
  return (
    <ChatProvider>
      <div className="flex flex-col h-screen w-screen bg-background overflow-hidden">
        {/* PM Sandbox Banner */}
        {isDemoMode && (<div className="bg-amber-500/60 backdrop-blur-sm text-black/80 px-4 py-1.5 text-center text-xs font-medium flex items-center justify-center gap-2 shrink-0">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>PM Sandbox Environment — For internal alignment and feature development only. This is not a staging or production application.</span>
        </div>)}

        <div className="flex flex-1 overflow-hidden">
          <Sidebar />
          <div className="flex-1 overflow-hidden w-full">
            <div className="h-full w-full overflow-auto bg-background">
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </ChatProvider>
  );
};

const router = createBrowserRouter([
  { path: "/error", Component: ErrorPage },
  {
    path: "/", Component: Auth0ProviderWithNavigate,
    loader: async ({ request }) => {
      // Get token from standalone Auth0 client
      const token = await getAccessTokenForLoader();

      if (!token) {
        // Not authenticated - trigger login
        await redirectToLogin(new URL(request.url).pathname);
        // Return null while redirecting (will never reach this)
        return null;
      }

      // Set up axios with the token getter for this session
      setTokenGetter(() => auth0Client.getTokenSilently());

      // Now make the authenticated API call
      const response = await getMeApiV3MeGet();

      if (isAxiosError(response)) {
        const status = response.response?.status ?? 500;
        const statusText = response.response?.statusText ?? 'Internal Server Error';
        throw json({ message: statusText }, { status });
      }

      // Make sure to set the data directly from here
      queryClient.setQueryData(getMeApiV3MeGetOptions().queryKey, response.data);

      return { org: response.headers['x-org-id'] };
    },
    errorElement: <ErrorPage />,
    // All routes with sidebar layout
    children: [
      {
        Component: AppLayout, children: [
          { index: true, element: <Navigate to="/chat" replace /> },
          { path: "chat", Component: LandingChatPage },
          { path: "chat/:conversationId", Component: LandingChatPage },
          { path: "chat-history", element: (<Navigate to="/chat" replace />) },
          {
            path: "dashboard", Component: DashboardLayout, children: [
              { index: true, element: <Navigate to="/dashboard/executive" replace /> },
              { path: "executive", Component: ExecutiveDashboard },
              {
                path: "deepdive", children: [
                  { index: true, element: <Navigate to="/dashboard/deepdive/awareness" replace /> },
                  { path: ":tab", Component: DeepDiveDashboard }

                ]
              },
              {
                path: "digital", children: [
                  { index: true, element: <Navigate to="/dashboard/digital/social" replace /> },
                  { path: ":tab", Component: DigitalMetricsDashboard }
                ]
              },
              { path: "comparison", Component: BrandComparison },
              { path: "posts", Component: PostsDashboard },
            ]
          },
          { path: "dashboard/:brandId", Component: BrandDetailPage },
          { path: "segments", Component: SegmentsPage },
          { path: "promotions", Component: PromotionsPage },
          { path: "insights", Component: InsightsPage },
        ]
      },
      { path: "*", Component: NotFoundPage },
    ]
  }
])

/**
 * Main App component for BrandHealth 360
 */
const App = () => {
  console.log('=== App.tsx Component Loaded ===');
  return <RouterProvider router={router} />
};

export default App;
